import styled from '@emotion/styled'
import { AdmRefreshGlobalSettings, AdmRefreshGlobalSettingsData, ExcludedCampaignsUnavailableReason } from '@pubstack/common/src/adm/refresh-global-settings'
import { formatHumanReadablePreviousDays } from '@pubstack/common/src/date'
import { LineItemTypeName } from '@pubstack/common/src/gam/lineItemType'
import { DateTime } from 'luxon'
import { FunctionComponent, useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { ContentCard } from '~/components/ContentCard'
import { Skeleton } from '~/components/Skeleton'
import { Tooltip } from '~/components/Tooltip'
import { WithClassName } from '~/types/utils'
import { GlobalSetting } from './components/GlobalSetting'
import { GlobalSettingView } from './components/GlobalSettingView'
import { LineItemTypeSelector } from './components/LineItemTypeSelector'
import { RefreshWrapper } from './PureAdmRefreshPage'

const BottomBar = styled.div`
  border-top: 1px solid ${Colors.Platinum};
`

const CardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const TitleWrapper = styled.div`
  display: flex;
  ${Fonts.H1};
  align-items: center;
  margin-right: auto;
`
const SubTitleWrapper = styled.h2`
  ${Fonts.H2};
  font-weight: 500;
`

const ActionsWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`

const LoadExcludedCampaignAction = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`
const LastUpdateText = styled.span`
  ${Fonts.P3};
  ${Fonts.colors.SlateGrey};
`

const ExclusionText = styled.div`
  margin-top: 4px;
  margin-bottom: 16px;
`

const SettingsHeaderAndFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const CardActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: end;
`

const GlobalSettingsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
`

const NoSettings = styled.div`
  font-style: italic;
  ${Fonts.colors.Ash};
`

const AddSettingButton = styled(Button)`
  margin-right: auto;
`

type PureAdmRefreshGlobalSettingsPageProps = WithClassName & {
  onLoadExcludedCampaigns: () => void
  globalSettings: AdmRefreshGlobalSettingsData
  onSaveGlobalSettings: (settings: AdmRefreshGlobalSettings, isFormDirty: boolean) => Promise<void>
  onSaveExcludedLineItemTypes: (lineItemTypes: LineItemTypeName[]) => void
  isLoading: boolean
  isPubstackRole: boolean
}

const CLIENT_UNAVAILABLE_MESSAGE = 'Campaign exclusions are not available yet: the GAM setup for your network is incomplete. Contact your account manager to enable this feature.'

const PUBSTACK_UNAVAILABLE_MESSAGES: Record<ExcludedCampaignsUnavailableReason, string> = {
  NO_GAM_INTEGRATION: 'No GAM network on this scope — adm_scope_config.networkId is 0.',
  NO_GAM_NETWORK: 'No gam_network_configs entry matching this scope and network code.',
  NO_EXCLUSION_LABEL:
    "refreshExclusionLabelId is missing on gam_network_configs. gam-manager only writes it when it creates the 'Pubstack No Refresh' label, never when the label already exists on the network.",
}

const LAMBDA_RUNNING_MESSAGE = 'An update is already running. You can leave the page and come back in a few minutes.'

const DEFAULT_VALUE: Omit<AdmRefreshGlobalSettings, 'id'> = {
  scopeId: '',
  settings: [
    {
      bidders: [],
      sizes: [],
    },
  ],
}

const UnstyledPureAdmRefreshGlobalSettingsPage: FunctionComponent<PureAdmRefreshGlobalSettingsPageProps> = ({
  globalSettings,
  onLoadExcludedCampaigns,
  onSaveGlobalSettings,
  isLoading,
  onSaveExcludedLineItemTypes,
  isPubstackRole,
}) => {
  const getLastUpdate = (timeMillis: number): string => `${formatHumanReadablePreviousDays(DateTime.fromMillis(timeMillis))} (Europe/Paris)`

  const formMethods = useForm<AdmRefreshGlobalSettings>({ mode: 'onSubmit', defaultValues: globalSettings.refreshGlobalSettings ?? DEFAULT_VALUE })
  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = formMethods
  const { fields, append, remove } = useFieldArray({ control, name: 'settings' })
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isLambdaLaunched, setIsLambdaLaunched] = useState<boolean>(globalSettings.excludedCampaignsData !== null && !globalSettings.excludedCampaignsData.canLambdaBeLaunched)

  // why "Load excluded campaigns" is disabled, or undefined when the button is actionable
  const loadExcludedCampaignsDisabledReason = ((): string | undefined => {
    if (isLoading) return undefined
    if (isLambdaLaunched) return LAMBDA_RUNNING_MESSAGE
    if (globalSettings.excludedCampaignsData !== null) return undefined
    return isPubstackRole && globalSettings.excludedCampaignsUnavailableReason ? PUBSTACK_UNAVAILABLE_MESSAGES[globalSettings.excludedCampaignsUnavailableReason] : CLIENT_UNAVAILABLE_MESSAGE
  })()

  const sanitize = (globalSettings: AdmRefreshGlobalSettings): AdmRefreshGlobalSettings => ({
    ...globalSettings,
    settings: globalSettings.settings.map((setting) => ({ ...setting, timer: setting.timer ? Number(setting.timer) : undefined })),
  })

  const onSubmit: SubmitHandler<AdmRefreshGlobalSettings> = async (globalSettingsForm) => {
    setIsSaving(true)
    await onSaveGlobalSettings(
      sanitize({
        ...globalSettingsForm,
        settings: globalSettingsForm.settings.map((setting) => ({
          ...setting,
          bidders: setting.bidders.map((bidder) => globalSettings.bidders.find((b) => b.displayName === bidder)?.codeOrAlias ?? ''),
        })),
      }),
      isDirty
    )
    setIsEditing(false)
    setIsSaving(false)
  }

  useEffect(() => {
    // syncing react-hook-form when the fetched settings land; reset() is an external-store write and cannot move into render
    if (globalSettings.refreshGlobalSettings !== null) {
      reset(globalSettings.refreshGlobalSettings)
    }
    // oxlint-disable-next-line react/set-state-in-effect -- same sync: derived from the settings that were just reset into the form
    setIsLambdaLaunched(globalSettings.excludedCampaignsData !== null && !globalSettings.excludedCampaignsData.canLambdaBeLaunched)
  }, [globalSettings])

  const cardActions = (
    <CardActions>
      <Button
        variant={'tertiary'}
        onClick={() => {
          reset()
          setIsEditing(false)
        }}
        trackingLabel={'adm-refresh-global-settings-cancel'}
      >
        Cancel
      </Button>
      <Button variant={'primary'} type={'submit'} disabled={isSaving} trackingLabel={'adm-refresh-global-settings-save'}>
        Save
      </Button>
    </CardActions>
  )

  return (
    <RefreshWrapper>
      <div>Following settings will be applied on refreshed stacks.</div>
      <ContentCard color={Colors.Petrol}>
        <CardContentWrapper>
          <TitleWrapper>
            <p>Campaigns exclusions via GAM</p>
          </TitleWrapper>
          <BottomBar />
          <div>
            <SubTitleWrapper>By line item types</SubTitleWrapper>

            <ExclusionText>
              <div>All line item types selected below will not be refreshed.</div>
            </ExclusionText>
            <div>No refresh on :</div>
            <LineItemTypeSelector selectedTypes={globalSettings.excludedLineItemTypes} onTypesSelection={onSaveExcludedLineItemTypes} />
            <div>
              <em>Want to refresh a specific line item, but it’s part of an excluded category ? Add "Allow Pubstack Refresh" to it in Google Ad Manager.</em>
            </div>
          </div>
          <div>
            <SubTitleWrapper>By advertisers, orders or line items</SubTitleWrapper>
            <ExclusionText>
              <div>Prevent refresh on selected advertisers, orders and/or line items by adding the label "Pubstack No Refresh" to them in Google Ad Manager.</div>
              <div>Label doesn't work at Creative level.</div>
            </ExclusionText>

            <ExclusionText>
              The exclusion list is automatically updated everyday between 11pm and 12am (Europe/Paris timezone). Update it manually if a running campaign needs to be excluded immediately.
            </ExclusionText>

            <ActionsWrapper>
              <LoadExcludedCampaignAction>
                <Tooltip title={loadExcludedCampaignsDisabledReason} disabled={!loadExcludedCampaignsDisabledReason} positions={['top']}>
                  <Button
                    variant={'secondary'}
                    disabled={isLoading || isLambdaLaunched || globalSettings.excludedCampaignsData === null}
                    iconSpin={isLambdaLaunched}
                    iconName={isLambdaLaunched ? 'pubstack' : 'restore'}
                    onClick={() => {
                      setIsLambdaLaunched(true)
                      onLoadExcludedCampaigns()
                    }}
                    trackingLabel={'adm-refresh-global-settings-excluded-campaign-loading-apply'}
                  >
                    Load excluded campaigns
                  </Button>
                </Tooltip>
                <LastUpdateText>
                  {isLoading || globalSettings.excludedCampaignsData === null ? (
                    ''
                  ) : isLambdaLaunched ? (
                    <span>Updating... This process can take few minutes. You can leave the page.</span>
                  ) : (
                    globalSettings.excludedCampaignsData.lastModified && <span>Last update: {getLastUpdate(globalSettings.excludedCampaignsData.lastModified)}</span>
                  )}
                </LastUpdateText>
              </LoadExcludedCampaignAction>
              <a href={`${globalSettings.excludedCampaignsData?.url}`} download rel={'noreferrer'} target={'_blank'}>
                <Button
                  variant={'tertiary'}
                  disabled={globalSettings.excludedCampaignsData === null || !globalSettings.excludedCampaignsData.lastModified}
                  iconName={'download'}
                  trackingLabel={'adm-refresh-global-settings-excluded-campaign-download'}
                >
                  See campaign list
                </Button>
              </a>
            </ActionsWrapper>
          </div>
        </CardContentWrapper>
      </ContentCard>
      <ContentCard color={Colors.Petrol}>
        {isEditing ? (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContentWrapper>
                <SettingsHeaderAndFooter>
                  <TitleWrapper>Settings</TitleWrapper>
                  {cardActions}
                </SettingsHeaderAndFooter>

                <BottomBar />
                <div>
                  <div>Define specific refresh settings for bidders, sizes, or bidders x sizes.</div>
                  <div>If an impression matches several rules, the longest timer will be used, which will ensure an optimal ad experience on your website.</div>
                </div>
                <GlobalSettingsWrapper>
                  {fields.map((field, index) => (
                    <GlobalSetting
                      key={field.id}
                      selectableBidders={globalSettings.bidders}
                      onDelete={(indexToDelete: number) => {
                        remove(indexToDelete)
                      }}
                      index={index}
                    />
                  ))}
                </GlobalSettingsWrapper>
                <SettingsHeaderAndFooter>
                  <AddSettingButton variant={'secondary'} iconName={'add'} onClick={() => append(DEFAULT_VALUE.settings, { shouldFocus: false })} trackingLabel={'adm-refresh-global-settings-new'}>
                    Add setting
                  </AddSettingButton>

                  {cardActions}
                </SettingsHeaderAndFooter>
              </CardContentWrapper>
            </form>
          </FormProvider>
        ) : (
          <CardContentWrapper>
            <SettingsHeaderAndFooter>
              <TitleWrapper>Settings</TitleWrapper>
              <CardActions>
                <Button variant={'primary'} onClick={() => setIsEditing(true)} disabled={isLoading} trackingLabel={'adm-refresh-global-settings-edit'}>
                  Edit
                </Button>
              </CardActions>
            </SettingsHeaderAndFooter>

            <BottomBar />
            <div>
              <div>Define specific refresh settings for bidders, sizes, or bidders x sizes.</div>
              <div>If an impression matches several rules, the longest timer will be used, which will ensure an optimal ad experience on your website.</div>
            </div>
            {isLoading ? (
              <Skeleton bigger={true} />
            ) : globalSettings.refreshGlobalSettings !== null ? (
              <GlobalSettingsWrapper>
                {globalSettings.refreshGlobalSettings?.settings.map((setting, index) => (
                  <GlobalSettingView key={`GlobalSettingView${index}`} refreshGlobalSetting={setting} selectableBidders={globalSettings.bidders} />
                ))}
              </GlobalSettingsWrapper>
            ) : (
              <NoSettings>No settings yet.</NoSettings>
            )}
          </CardContentWrapper>
        )}
      </ContentCard>
    </RefreshWrapper>
  )
}

export const PureAdmRefreshGlobalSettingsPage = styled(UnstyledPureAdmRefreshGlobalSettingsPage)``
